import React, { useState, useRef, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { leerArchivo, validarColumnas, contarInvalidos, subirLeads, descargarFallos, getCampaniasActivas, COLUMNAS } from '../../servicios/CargaLeads';
import './cargar.css';

const EXTENSIONES = ['.csv', '.xlsx', '.xls'];

export default function Cargar() {
    const [destino, setDestino] = useState('movistar');
    const [archivo, setArchivo] = useState(null);
    const [filas, setFilas] = useState(null);
    const [invalidosPrevios, setInvalidosPrevios] = useState(0);
    const [estado, setEstado] = useState('inicial'); // inicial | listo | subiendo | fin
    const [progreso, setProgreso] = useState(null);
    const [arrastrando, setArrastrando] = useState(false);
    const [campanias, setCampanias] = useState([]);
    const [campaniaSel, setCampaniaSel] = useState('');   // '' = nombre automático por fecha
    const inputRef = useRef(null);

    // Carga las campañas activas del año (del destino elegido) para etiquetar la base.
    useEffect(() => {
        setCampaniaSel('');
        getCampaniasActivas(destino).then(setCampanias).catch(() => setCampanias([]));
    }, [destino]);

    // Procesa el archivo venga del input o del arrastre.
    const procesarArchivo = async (file) => {
        if (!file) return;

        const nombre = file.name.toLowerCase();
        if (!EXTENSIONES.some((ext) => nombre.endsWith(ext))) {
            toast.error(`Formato no válido. Debe ser un archivo ${EXTENSIONES.join(', ')}.`);
            return;
        }

        setArchivo(file);
        setFilas(null);
        setInvalidosPrevios(0);
        setEstado('inicial');

        try {
            const { headers, filas } = await leerArchivo(file);

            const errCol = validarColumnas(headers);
            if (errCol) {
                toast.error(errCol, { autoClose: 7000 });
                setArchivo(null);
                if (inputRef.current) inputRef.current.value = '';
                return;
            }
            if (filas.length === 0) {
                toast.warn('El archivo no tiene filas de datos.');
                setArchivo(null);
                return;
            }

            // Pre-validación: avisar de celulares mal formados antes de subir.
            const malos = contarInvalidos(filas);
            setInvalidosPrevios(malos);
            if (malos === filas.length) {
                toast.error('Ningún celular tiene el formato correcto (09XXXXXXXX). Revisa el archivo.', { autoClose: 7000 });
                setArchivo(null);
                return;
            }
            if (malos > 0) {
                toast.warn(`${malos} de ${filas.length} celulares no tienen formato válido y se omitirán.`, { autoClose: 6000 });
            }

            setFilas(filas);
            setEstado('listo');
            toast.success(`Archivo válido: ${filas.length.toLocaleString()} registros.`);
        } catch (ex) {
            toast.error(ex.message);
            setArchivo(null);
        }
    };

    const onArchivo = (e) => procesarArchivo(e.target.files[0]);

    // --- Arrastrar y soltar ---
    const onDragOver = (e) => { e.preventDefault(); if (estado !== 'subiendo') setArrastrando(true); };
    const onDragLeave = (e) => { e.preventDefault(); setArrastrando(false); };
    const onDrop = (e) => {
        e.preventDefault();
        setArrastrando(false);
        if (estado === 'subiendo') return;
        const file = e.dataTransfer.files && e.dataTransfer.files[0];
        procesarArchivo(file);
    };

    const subir = async () => {
        if (!filas) return;
        setEstado('subiendo');
        setProgreso({ procesadas: 0, insertadas: 0, actualizadas: 0, invalidas: 0, totalFilas: filas.length });
        try {
            const res = await subirLeads(filas, setProgreso, destino, campaniaSel);
            setProgreso(res);
            setEstado('fin');
            toast.success('Carga completada');
        } catch (ex) {
            toast.error('Error en la carga: ' + ex.message);
            setEstado('listo');
        }
    };

    const reiniciar = () => {
        setArchivo(null); setFilas(null); setEstado('inicial'); setProgreso(null); setInvalidosPrevios(0);
        if (inputRef.current) inputRef.current.value = '';
    };

    const pct = progreso && progreso.totalFilas
        ? Math.round((progreso.procesadas / progreso.totalFilas) * 100) : 0;

    return (
        <div className="carga-wrap">
            <ToastContainer position="top-right" autoClose={4000} />
            <div className="carga-card">
                <h2 className="carga-title">Cargar base de leads</h2>
                <p className="carga-sub">
                    Sube un archivo <strong>Excel o CSV</strong> con las columnas:{' '}
                    <code>{COLUMNAS.join(', ')}</code>. Los números que ya existan se
                    <strong> actualizan</strong>; los nuevos se agregan.
                </p>

                {estado !== 'fin' && (
                    <div className="carga-destino">
                        <span className="carga-destino__label">Subir a la base de:</span>
                        <div className="carga-destino__ops">
                            <button
                                type="button"
                                className={`carga-destino__op ${destino === 'movistar' ? 'activo' : ''}`}
                                onClick={() => setDestino('movistar')}
                                disabled={estado === 'subiendo'}
                            >Tigo</button>
                            <button
                                type="button"
                                className={`carga-destino__op ${destino === 'claro' ? 'activo claro' : ''}`}
                                onClick={() => setDestino('claro')}
                                disabled={estado === 'subiendo'}
                            >Claro</button>
                        </div>
                        <div className="carga-campania">
                            <span className="carga-destino__label">Campaña (etiqueta de la base):</span>
                            <select
                                className="carga-campania__sel"
                                value={campaniaSel}
                                onChange={(e) => setCampaniaSel(e.target.value)}
                                disabled={estado === 'subiendo'}
                            >
                                <option value="">— Automático por fecha —</option>
                                {campanias.map((c) => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
                            </select>
                            <small className="carga-campania__hint">Solo campañas activas creadas este año. La base se guarda con esta etiqueta.</small>
                        </div>
                    </div>
                )}

                {estado !== 'fin' && (
                    <div
                        className={`carga-drop ${arrastrando ? 'arrastrando' : ''}`}
                        onDragOver={onDragOver}
                        onDragLeave={onDragLeave}
                        onDrop={onDrop}
                    >
                        <input
                            ref={inputRef}
                            type="file"
                            accept=".csv,.xlsx,.xls"
                            id="archivo-leads"
                            className="carga-input"
                            onChange={onArchivo}
                            disabled={estado === 'subiendo'}
                        />
                        <label htmlFor="archivo-leads" className="carga-label">
                            <i className="bi bi-cloud-arrow-up"></i>
                            <span>{archivo ? archivo.name : 'Arrastra el archivo aquí o haz clic para seleccionar'}</span>
                            <small>Formatos: Excel (.xlsx, .xls) o CSV</small>
                        </label>
                    </div>
                )}

                {estado === 'listo' && (
                    <div className="carga-listo">
                        <p>
                            <strong>{filas.length.toLocaleString()}</strong> registros listos
                            {invalidosPrevios > 0 && <span className="carga-aviso"> ({invalidosPrevios} se omitirán por celular inválido)</span>}.
                        </p>
                        <button className="carga-btn" onClick={subir}>Subir base</button>
                    </div>
                )}

                {estado === 'subiendo' && progreso && (
                    <div className="carga-progreso">
                        <div className="carga-bar"><div className="carga-bar__fill" style={{ width: pct + '%' }} /></div>
                        <p>{progreso.procesadas.toLocaleString()} / {progreso.totalFilas.toLocaleString()} ({pct}%)</p>
                    </div>
                )}

                {estado === 'fin' && progreso && (
                    <div className="carga-resumen">
                        <h3>Resumen</h3>
                        <div className="carga-stat"><span>Destino</span><strong>{destino === 'claro' ? 'Claro (Fenix)' : 'Tigo (Dragon)'}</strong></div>
                        <div className="carga-stat"><span>Lote</span><strong>{progreso.dbName}</strong></div>
                        <div className="carga-stat carga-ok"><span>Nuevos</span><strong>{progreso.insertadas.toLocaleString()}</strong></div>
                        <div className="carga-stat carga-upd"><span>Actualizados</span><strong>{progreso.actualizadas.toLocaleString()}</strong></div>
                        <div className="carga-stat carga-bad"><span>Inválidos</span><strong>{progreso.invalidas.toLocaleString()}</strong></div>

                        {progreso.invalidas > 0 && progreso.fallos && (
                            <button className="carga-btn carga-btn--descarga" onClick={() => descargarFallos(progreso.fallos)}>
                                <i className="bi bi-download"></i> Descargar filas con error ({progreso.invalidas})
                            </button>
                        )}

                        <button className="carga-btn carga-btn--sec" onClick={reiniciar}>Cargar otra base</button>
                    </div>
                )}
            </div>
        </div>
    );
}
